import React, { useState, useEffect } from 'react';
import { Building2, ShieldCheck, FileText, BarChart3, LayoutDashboard, MessageSquare, Upload, Palette, ChevronRight, Menu, X, Wand2, ArrowRight, Users, TrendingUp, PenTool, Table, Lock, CheckCircle2 } from 'lucide-react';

export default function LandingB2B() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans antialiased">
      {/* Nav */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wand2 className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold tracking-tight">wizardly.ai</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-600">
            <button onClick={() => scrollTo('platform')} className="hover:text-gray-900 transition-colors">Platform</button>
            <button onClick={() => scrollTo('use-cases')} className="hover:text-gray-900 transition-colors">Use Cases</button>
            <button onClick={() => scrollTo('security')} className="hover:text-gray-900 transition-colors">Security</button>
            <button onClick={() => scrollTo('faq')} className="hover:text-gray-900 transition-colors">FAQ</button>
            <a href="https://prezlab-brain.netlify.app" className="ml-2 px-5 py-2.5 bg-indigo-600 text-white rounded-full text-sm font-medium hover:bg-indigo-700 transition-colors">
              Book a Demo
            </a>
          </div>
          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t px-6 py-4 space-y-3">
            <button onClick={() => scrollTo('platform')} className="block w-full text-left text-sm text-gray-600 hover:text-gray-900">Platform</button>
            <button onClick={() => scrollTo('use-cases')} className="block w-full text-left text-sm text-gray-600 hover:text-gray-900">Use Cases</button>
            <button onClick={() => scrollTo('security')} className="block w-full text-left text-sm text-gray-600 hover:text-gray-900">Security</button>
            <button onClick={() => scrollTo('faq')} className="block w-full text-left text-sm text-gray-600 hover:text-gray-900">FAQ</button>
            <a href="https://prezlab-brain.netlify.app" className="block w-full text-center px-5 py-2.5 bg-indigo-600 text-white rounded-full text-sm font-medium">Book a Demo</a>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium mb-8">
            <Building2 className="w-4 h-4" />
            Built for teams and organizations
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
            Every team member.{' '}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">Designer-quality output.</span>{' '}
            Zero design dependency.
          </h1>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            wizardly.ai lets your entire organization produce on-brand presentations, reports, and dashboards through conversation — freeing your design team from the formatting work they shouldn't be doing.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <a href="https://prezlab-brain.netlify.app" className="px-8 py-4 bg-indigo-600 text-white rounded-full text-lg font-semibold hover:bg-indigo-700 transition-all hover:shadow-lg hover:shadow-indigo-200">
              Book a Demo
            </a>
            <a href="https://prezlab-brain.netlify.app" className="px-8 py-4 text-gray-600 rounded-full text-lg font-medium hover:text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2">
              Try It Free <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Hero product screenshot placeholder */}
        <div className="max-w-5xl mx-auto mt-16">
          <div className="relative rounded-2xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white shadow-2xl shadow-gray-200/50 overflow-hidden">
            <div className="aspect-[16/9] flex items-center justify-center text-gray-300">
              <div className="text-center">
                <LayoutDashboard className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-sm text-gray-400">Product screenshot — branded deck with AI plan and variation picker</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Your smartest people are your worst bottleneck</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <ProblemCard
              title="The Hidden Cost"
              text="Your senior analyst spends 6 hours on a quarterly deck. Your strategist rebuilds the same proposal layout every week. Your design team's backlog is 80% 'can you make this look good?' The talent is there. The tooling isn't."
              icon={<TrendingUp className="w-6 h-6" />}
            />
            <ProblemCard
              title="The Brand Problem"
              text="Every department makes their own slides. Sales uses one color palette, marketing uses another, and the regional team gave up on the brand guide entirely. You've sent the style guide three times. Nobody reads it."
              icon={<Palette className="w-6 h-6" />}
            />
            <ProblemCard
              title="The Speed Problem"
              text="A client asks for a proposal by Friday. The content is ready Monday. But getting it into a presentable format takes until Thursday — because the design team is working on next quarter's campaign. The bottleneck isn't thinking. It's formatting."
              icon={<BarChart3 className="w-6 h-6" />}
            />
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What your team looks like with wizardly.ai</h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            <ValueProp
              icon={<ShieldCheck className="w-7 h-7" />}
              title="Brand Compliance Without Enforcement"
              text="Upload your brand system once — colors, fonts, logos, layout rules. Every output anyone generates inherits it automatically. No review cycle. No 'can you fix the colors?' emails. The brand is built into the tool, not policed after the fact."
            />
            <ValueProp
              icon={<Users className="w-7 h-7" />}
              title="Design Team Unlocked"
              text="When every employee can produce a polished deck on their own, your designers stop being a formatting service. They go back to the work you actually hired them for — campaigns, product launches, the creative work that moves the needle."
            />
            <ValueProp
              icon={<MessageSquare className="w-7 h-7" />}
              title="Strategic Output, Not Template Output"
              text="The AI Consultant doesn't just arrange content on slides. It asks about the audience, recommends a narrative structure, and builds a content plan before generating anything. Your team gets deliverables that make arguments — not decks that fill space."
            />
          </div>
        </div>
      </section>

      {/* Platform Capabilities */}
      <section id="platform" className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">One platform, every deliverable</h2>
          </div>
          <div className="space-y-4">
            <CapabilityRow icon={<MessageSquare className="w-5 h-5" />} title="AI Consultant" value="Non-designers get strategic content guidance — fewer revision rounds, fewer 'this doesn't flow' conversations" />
            <CapabilityRow icon={<Palette className="w-5 h-5" />} title="Brand Identity System" value="One source of truth for visual identity — enforced automatically, not manually" />
            <CapabilityRow icon={<FileText className="w-5 h-5" />} title="5 Output Formats" value="Decks, reports, infographics, dashboards, spreadsheets — one tool replaces four" />
            <CapabilityRow icon={<LayoutDashboard className="w-5 h-5" />} title="Design Variations" value="Stakeholders pick from 3 options instead of requesting 3 rounds of revisions" />
            <CapabilityRow icon={<Upload className="w-5 h-5" />} title="File to Deliverable" value="Drop a brief, report, or dataset — get a presentation-ready output back" />
            <CapabilityRow icon={<PenTool className="w-5 h-5" />} title="Chat-Based Editing" value="Anyone can refine results through natural language — no design tool training needed" />
            <CapabilityRow icon={<CheckCircle2 className="w-5 h-5" />} title="Fact Verification" value="AI-generated claims are checked against source material before delivery" />
          </div>
        </div>
      </section>

      {/* Use Cases by Department */}
      <section id="use-cases" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">From the boardroom to the sales floor</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <DeptCard
              dept="Sales"
              before="4 hours per custom proposal, inconsistent branding"
              after="Tailored pitch deck per prospect in 15 minutes, always on-brand"
            />
            <DeptCard
              dept="Marketing"
              before="Campaign reports delayed by design queue"
              after="Self-serve dashboards and infographics, same day"
            />
            <DeptCard
              dept="Finance"
              before="Board decks assembled manually from spreadsheets"
              after="Upload the numbers, get the narrative and visuals together"
            />
            <DeptCard
              dept="Operations"
              before="Quarterly reviews look different every time"
              after="Standardized, branded reporting across regions"
            />
            <DeptCard
              dept="HR & L&D"
              before="Training decks outsourced or DIY'd poorly"
              after="Professional onboarding and training materials, built in-house"
            />
            <DeptCard
              dept="Leadership"
              before="All-hands decks take a full design sprint"
              after="Strategy presentations from notes to final in one sitting"
            />
          </div>
        </div>
      </section>

      {/* Security */}
      <section id="security" className="py-24 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Enterprise-ready</h2>
            <p className="text-lg text-gray-500">Built for organizations that care about data</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <SecurityCard icon={<Lock className="w-5 h-5" />} title="Encryption" text="Data encrypted in transit and at rest" />
            <SecurityCard icon={<ShieldCheck className="w-5 h-5" />} title="No Training on Your Data" text="Your content is processed for generation only — never retained for model training" />
            <SecurityCard icon={<Users className="w-5 h-5" />} title="Team Management" text="Role-based access controls and team administration" />
            <SecurityCard icon={<FileText className="w-5 h-5" />} title="Compliance" text="SOC 2 compliance planned — security documentation available for procurement review" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">Frequently asked questions</h2>
          <div className="space-y-6">
            <FAQItem
              q="How is this different from Gamma / Tome / Beautiful.ai?"
              a="Template-based tools fill in layouts. wizardly.ai starts with content analysis — understanding audience, narrative arc, and data relationships before designing. The AI Consultant functions like a junior strategist, not a layout engine."
            />
            <FAQItem
              q="Can we enforce our brand across all users?"
              a="Yes. One brand upload, automatic inheritance across every output. No user training needed."
            />
            <FAQItem
              q="What about sensitive data?"
              a="Content is processed for generation and not retained for training. We can provide security documentation for your procurement review."
            />
            <FAQItem
              q="Can teams trial it without procurement?"
              a="Yes. Start on the free tier with no approval needed. Scale up when you're ready."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-gradient-to-br from-indigo-600 to-violet-700">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Your team already has the content. Give them the tool to present it.</h2>
          <p className="text-lg text-indigo-200 mb-10">Stop waiting on the design queue. Give your team the ability to produce professional, on-brand work — instantly.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://prezlab-brain.netlify.app" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-indigo-700 rounded-full text-lg font-semibold hover:bg-indigo-50 transition-colors shadow-lg">
              Book a Demo <ArrowRight className="w-5 h-5" />
            </a>
            <a href="https://prezlab-brain.netlify.app" className="inline-flex items-center gap-2 px-8 py-4 text-white/90 rounded-full text-lg font-medium hover:text-white hover:bg-white/10 transition-colors">
              Or try it free
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold">wizardly.ai</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <span>Platform</span>
            <span>Security</span>
            <span>Privacy</span>
            <span>Terms</span>
            <span>Contact</span>
          </div>
          <p className="text-sm text-gray-400">&copy; 2026 Prezlab. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

/* Sub-components */

function ProblemCard({ title, text, icon }) {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="w-12 h-12 rounded-xl bg-red-50 text-red-500 flex items-center justify-center mb-5">{icon}</div>
      <h3 className="text-lg font-bold mb-3">{title}</h3>
      <p className="text-gray-500 leading-relaxed text-sm">{text}</p>
    </div>
  );
}

function ValueProp({ icon, title, text }) {
  return (
    <div className="bg-indigo-50/50 rounded-2xl p-8 border border-indigo-100">
      <div className="w-12 h-12 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mb-5">{icon}</div>
      <h3 className="text-lg font-bold mb-3">{title}</h3>
      <p className="text-gray-500 leading-relaxed text-sm">{text}</p>
    </div>
  );
}

function CapabilityRow({ icon, title, value }) {
  return (
    <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-gray-100">
      <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <h3 className="font-bold mb-1">{title}</h3>
        <p className="text-sm text-gray-500">{value}</p>
      </div>
    </div>
  );
}

function DeptCard({ dept, before, after }) {
  return (
    <div className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow">
      <h3 className="font-bold text-lg mb-4 text-indigo-600">{dept}</h3>
      <div className="space-y-3">
        <div>
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Before</span>
          <p className="text-sm text-gray-500 mt-1">{before}</p>
        </div>
        <div className="border-t border-gray-100 pt-3">
          <span className="text-xs font-medium text-indigo-500 uppercase tracking-wider">After</span>
          <p className="text-sm text-gray-700 mt-1 font-medium">{after}</p>
        </div>
      </div>
    </div>
  );
}

function SecurityCard({ icon, title, text }) {
  return (
    <div className="flex items-start gap-4 bg-white rounded-xl p-6 border border-gray-100">
      <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <h3 className="font-bold mb-1">{title}</h3>
        <p className="text-sm text-gray-500">{text}</p>
      </div>
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors">
        <span className="font-semibold text-gray-900">{q}</span>
        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 text-gray-500 leading-relaxed">{a}</div>
      )}
    </div>
  );
}
